/**
 * Templates (contracts templates.ts): the gallery's search and categories, who may save templates, row templates
 * (default cells for new rows, shared with the server's createRow) and the names of duplicates. Pure.
 */
import type {
  Database,
  Member,
  PageTemplateInfo,
  PropertyDef,
  PropertyValue,
  RowTemplate,
  TemplateCategory,
  TemplateSettings,
} from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { matchScore } from "./search.ts";

/** Gallery order of the built-in categories. */
export const TEMPLATE_CATEGORIES: TemplateCategory[] = ["projects", "meetings", "knowledge", "personal", "business"];

/** Gallery filter: everything, my saved templates (workspace + personal), or one category. */
export type TemplateFilter = "all" | "saved" | TemplateCategory;

export type TemplateSection = { id: "results" | "saved" | TemplateCategory; title: MessageDescriptor; templates: PageTemplateInfo[] };

const CATEGORY_TITLES: Record<TemplateCategory, MessageDescriptor> = {
  projects: msg("templates.category.projects"),
  meetings: msg("templates.category.meetings"),
  knowledge: msg("templates.category.knowledge"),
  personal: msg("templates.category.personal"),
  business: msg("templates.category.business"),
};

export function categoryTitle(category: TemplateCategory): MessageDescriptor {
  return CATEGORY_TITLES[category];
}

function isSaved(t: PageTemplateInfo): boolean {
  return t.scope !== "builtin";
}

/** Relevance of a template for a query: the name counts most, then the description, then the category id. */
export function templateScore(t: PageTemplateInfo, query: string): number {
  if (query.trim() === "") return 1;
  const name = matchScore(query, t.name);
  const description = matchScore(query, t.description);
  const best = Math.max(name, description >= 40 ? description - 20 : 0, matchScore(query, t.category) >= 80 ? 15 : 0);
  return best >= 10 ? best : 0;
}

/**
 * Templates matching `query` within `filter`. Without a query: built-ins in catalogue order, saved templates newest
 * first. With one: best match first (ties keep that order).
 */
export function filterTemplates(templates: PageTemplateInfo[], query: string, filter: TemplateFilter): PageTemplateInfo[] {
  const inFilter = templates.filter((t) => (filter === "all" ? true : filter === "saved" ? isSaved(t) : t.category === filter));
  const ordered = inFilter
    .map((t, i) => ({ t, i }))
    .toSorted((a, b) => {
      const sa = isSaved(a.t) ? 1 : 0;
      const sb = isSaved(b.t) ? 1 : 0;
      if (sa !== sb) return sa - sb;
      if (sa === 1) return (b.t.updatedAt ?? 0) - (a.t.updatedAt ?? 0) || a.i - b.i;
      return a.i - b.i;
    })
    .map((x) => x.t);
  if (query.trim() === "") return ordered;
  return ordered
    .map((t, i) => ({ t, i, s: templateScore(t, query) }))
    .filter((x) => x.s > 0)
    .toSorted((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.t);
}

/**
 * The gallery list: "Saved templates" first, then each category in catalogue order (empty sections omitted). With a
 * query the matches come as one section ("Results"), best first.
 */
export function templateSections(templates: PageTemplateInfo[], query: string, filter: TemplateFilter): TemplateSection[] {
  const list = filterTemplates(templates, query, filter);
  if (query.trim() !== "") return list.length === 0 ? [] : [{ id: "results", title: msg("templates.results"), templates: list }];
  const out: TemplateSection[] = [];
  const saved = list.filter(isSaved);
  if (saved.length > 0) out.push({ id: "saved", title: msg("templates.section.saved"), templates: saved });
  for (const category of TEMPLATE_CATEGORIES) {
    const items = list.filter((t) => !isSaved(t) && t.category === category);
    if (items.length > 0) out.push({ id: category, title: CATEGORY_TITLES[category], templates: items });
  }
  return out;
}

// ---------- Permissions ----------

function active(m: Member, now: number): boolean {
  if (m.deactivated) return false;
  const exp = m.access === undefined ? undefined : m.access.expiresAt;
  return exp === undefined || exp > now;
}

function restricted(m: Member): boolean {
  return m.role === "guest" || m.kind === "agent" || m.access?.level === "restricted";
}

function admin(m: Member): boolean {
  return m.role === "owner" || m.role === "admin";
}

/**
 * May save a template of this scope: personal templates every active member; workspace templates owners and admins,
 * or every member when the workspace allows it. Never guests or agents.
 */
export function canSaveTemplate(me: Member, settings: TemplateSettings, scope: "workspace" | "personal", now: number): boolean {
  if (!active(me, now) || restricted(me)) return false;
  if (scope === "personal") return true;
  return admin(me) || settings.workspaceTemplateCreators === "members";
}

/** May rename, edit or delete a saved template: its creator, and owners/admins for workspace templates. */
export function canManageTemplate(template: { scope: "workspace" | "personal"; createdBy: string }, me: Member, now: number): boolean {
  if (!active(me, now) || restricted(me)) return false;
  if (template.createdBy === me.userId) return true;
  return template.scope === "workspace" && admin(me);
}

/** May change the template settings (who saves workspace templates). */
export function canManageTemplateSettings(me: Member, now: number): boolean {
  return active(me, now) && !restricted(me) && admin(me);
}

// ---------- Row templates ----------

/** Row templates in "New ▾" order (rank, then id). */
export function rowTemplatesInOrder(db: Database): RowTemplate[] {
  return (db.rowTemplates ?? []).toSorted((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The template "New" uses, if it still exists. */
export function defaultRowTemplate(db: Database): RowTemplate | undefined {
  const id = db.defaultRowTemplateId;
  return id === undefined ? undefined : (db.rowTemplates ?? []).find((t) => t.id === id);
}

const COMPUTED = new Set(["created_time", "created_by", "edited_time", "edited_by"]);

/** A default cell that still fits the property (type, options), else undefined. */
function fits(prop: PropertyDef | undefined, v: PropertyValue): PropertyValue | undefined {
  if (prop === undefined || COMPUTED.has(prop.type) || prop.type !== v.type) return undefined;
  if (v.type === "select") return prop.options?.some((o) => o.id === v.optionId) === true ? v : undefined;
  if (v.type === "status") return prop.statusOptions?.some((o) => o.id === v.optionId) === true ? v : undefined;
  if (v.type === "multi_select") {
    const ids = v.optionIds.filter((id) => prop.options?.some((o) => o.id === id) === true);
    return ids.length === 0 ? undefined : ids.length === v.optionIds.length ? v : { type: "multi_select", optionIds: ids };
  }
  if ((v.type === "title" || v.type === "text") && v.text === "") return undefined;
  return v;
}

/**
 * A row template's defaults that still fit the schema: cells of removed properties, of properties whose type changed,
 * with removed options, or of computed properties are dropped.
 */
export function rowTemplateValues(db: Database, template: RowTemplate): Record<string, PropertyValue> {
  const out: Record<string, PropertyValue> = {};
  for (const [id, v] of Object.entries(template.values)) {
    const ok = fits(
      db.properties.find((p) => p.id === id),
      v,
    );
    if (ok !== undefined) out[id] = ok;
  }
  return out;
}

/**
 * Cells of a row created from a template: the template's defaults, with `values` (what the row was added with: the
 * board column, the calendar day, the view's filters, a typed title) taking precedence.
 */
export function applyRowTemplate(db: Database, template: RowTemplate | undefined, values: Record<string, PropertyValue>): Record<string, PropertyValue> {
  if (template === undefined) return { ...values };
  return { ...rowTemplateValues(db, template), ...values };
}

// ---------- Duplicates ----------

/**
 * The title of a copy: `pattern` with `{name}` replaced ("Copy of {name}"; an empty title uses `untitled`). When a
 * sibling already has that title, " (2)", " (3)"… is added.
 */
export function duplicateTitle(title: string, siblingTitles: string[], pattern: string, untitled: string): string {
  const name = title.trim() === "" ? untitled : title.trim();
  const base = pattern.includes("{name}") ? pattern.replace("{name}", name) : `${pattern} ${name}`;
  const taken = new Set(siblingTitles.map((s) => s.trim()));
  if (!taken.has(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} (${n})`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base} (${siblingTitles.length + 1})`;
}
